<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import imgClose from '@/assets/Fill 1@2x.png'
import imgDown from '@/assets/downarrow@2x.png'
import errorIcon from '@/assets/alert.png'
import ModalButtonSpinner from '@/components/ModalButtonSpinner.vue'
import {
  formatRefundMoney,
  postCareOrderRefundPreview,
  type RefundTaxMode,
} from '@/api/careOrderRefund'

const ERR_GT_ZERO = 'The refund amount must be greater than 0.'
const ERR_EXCEED =
  'The total refund amount cannot exceed the actual amount received for the order.'

export type { RefundTaxMode }

export type RefundSubmitPayload = {
  amount: number
  taxMode: RefundTaxMode
  note: string
}

export type RefundTaxOption = { value: RefundTaxMode; label: string }

const NOTE_MAX = 200
const PREVIEW_DEBOUNCE_MS = 300

const DEFAULT_TAX_OPTIONS: RefundTaxOption[] = [
  { value: 'exclusive_of_tax', label: 'Exclusive of Tax' },
  { value: 'inclusive_of_tax', label: 'Inclusive of Tax' },
  { value: 'out_of_scope', label: 'Out of Scope (0%)' },
]

const props = withDefaults(
  defineProps<{
    visible: boolean
    orderPrimaryId?: number | null
    lineIndex?: number
    amountPaid?: string
    refundableAmount?: string
    refundedAmount?: string
    taxRate?: number
    taxOptions?: RefundTaxOption[]
    submitting?: boolean
  }>(),
  {
    orderPrimaryId: null,
    lineIndex: 0,
    amountPaid: '$0.00',
    refundableAmount: '$0.00',
    refundedAmount: '$0.00',
    taxRate: 0,
    taxOptions: () => [],
    submitting: false,
  },
)

const emit = defineEmits<{
  close: []
  submit: [payload: RefundSubmitPayload]
  toast: [payload: { type: 'success' | 'warning' | 'error'; message: string }]
}>()

const amountText = ref('')
const taxMode = ref<RefundTaxMode>('inclusive_of_tax')
const note = ref('')
const taxDropdownOpen = ref(false)
const taxSelectWrapRef = ref<HTMLElement | null>(null)
/** 校验失败文案；有值时金额框显示红框+图标，文案展示在 Total 下方 */
const amountErrors = ref<string[]>([])
const hasAmountError = computed(() => amountErrors.value.length > 0)
const previewTotalDisplay = ref('')
const previewLoading = ref(false)
let previewTimer: ReturnType<typeof setTimeout> | null = null
let previewGeneration = 0

const resolvedTaxOptions = computed(() =>
  props.taxOptions.length ? props.taxOptions : DEFAULT_TAX_OPTIONS,
)

const taxModeLabel = computed(
  () =>
    resolvedTaxOptions.value.find((o) => o.value === taxMode.value)?.label ?? 'Inclusive of Tax',
)

function parseMoney(raw: string | undefined): number {
  if (!raw) return 0
  const n = Number(String(raw).replace(/[^0-9.-]/g, ''))
  return Number.isFinite(n) ? n : 0
}

function formatMoney(n: number): string {
  if (!Number.isFinite(n)) return '$0.00'
  return `$${n.toFixed(2)}`
}

const refundableNum = computed(() => parseMoney(props.refundableAmount))

const amountNum = computed(() => parseMoney(amountText.value))

const hasValidAmount = computed(() => amountText.value.trim().length > 0 && amountNum.value > 0)

const totalDisplay = computed(() => {
  if (!hasValidAmount.value) return ''
  return previewTotalDisplay.value
})

const noteCount = computed(() => note.value.length)

type OverflowSnapshot = {
  html: string
  body: string
  main: string | null
}

let overflowSnapshot: OverflowSnapshot | null = null

function lockBackgroundScroll() {
  if (overflowSnapshot) return
  const html = document.documentElement
  const body = document.body
  const main = document.querySelector('.main-content') as HTMLElement | null
  overflowSnapshot = {
    html: html.style.overflow,
    body: body.style.overflow,
    main: main ? main.style.overflow : null,
  }
  html.style.overflow = 'hidden'
  body.style.overflow = 'hidden'
  if (main) main.style.overflow = 'hidden'
}

function unlockBackgroundScroll() {
  if (!overflowSnapshot) return
  const html = document.documentElement
  const body = document.body
  const main = document.querySelector('.main-content') as HTMLElement | null
  html.style.overflow = overflowSnapshot.html
  body.style.overflow = overflowSnapshot.body
  if (main && overflowSnapshot.main !== null) {
    main.style.overflow = overflowSnapshot.main
  }
  overflowSnapshot = null
}

function clearPreviewTimer() {
  if (previewTimer != null) {
    clearTimeout(previewTimer)
    previewTimer = null
  }
}

function resetForm() {
  amountText.value = ''
  taxMode.value = 'inclusive_of_tax'
  note.value = ''
  amountErrors.value = []
  taxDropdownOpen.value = false
  previewTotalDisplay.value = ''
  previewLoading.value = false
  clearPreviewTimer()
  previewGeneration += 1
}

function buildLocalPreviewFallback(): string {
  const amount = amountNum.value
  if (amount <= 0) return ''
  if (taxMode.value === 'out_of_scope') {
    return `${formatMoney(amount)} (including Refund Tax $0.00)`
  }
  const rate = props.taxRate > 0 ? props.taxRate : 0
  let tax = 0
  if (rate > 0) {
    tax =
      taxMode.value === 'inclusive_of_tax' ? amount * (rate / (1 + rate)) : amount * rate
  }
  const total = taxMode.value === 'exclusive_of_tax' ? amount + tax : amount
  return `${formatMoney(total)} (including Refund Tax ${formatMoney(tax)})`
}

async function runPreview(): Promise<void> {
  if (!props.visible || !hasValidAmount.value) {
    previewTotalDisplay.value = ''
    return
  }

  const pk = props.orderPrimaryId
  if (pk == null || !Number.isFinite(pk) || pk <= 0) {
    previewTotalDisplay.value = buildLocalPreviewFallback()
    return
  }

  const generation = ++previewGeneration
  previewLoading.value = true
  try {
    const response = await postCareOrderRefundPreview({
      id: pk,
      line_index: props.lineIndex,
      amount: amountNum.value,
      tax_type: taxMode.value,
    })
    if (generation !== previewGeneration) return

    if (response.code === 200 && response.data) {
      const data = response.data
      const text =
        (typeof data.total_text === 'string' && data.total_text.trim()) ||
        (typeof data.total_display === 'string' && data.total_display.trim()) ||
        ''
      if (text) {
        previewTotalDisplay.value = text
        return
      }
      const total = formatRefundMoney(data.total_amount ?? data.total)
      const tax = formatRefundMoney(data.tax_amount ?? data.refund_tax ?? data.tax)
      previewTotalDisplay.value = `${total} (including Refund Tax ${tax})`
      return
    }
    previewTotalDisplay.value = buildLocalPreviewFallback()
  } catch (err) {
    if (generation !== previewGeneration) return
    console.error('Refund preview error:', err)
    previewTotalDisplay.value = buildLocalPreviewFallback()
  } finally {
    if (generation === previewGeneration) {
      previewLoading.value = false
    }
  }
}

function schedulePreview() {
  clearPreviewTimer()
  if (!hasValidAmount.value) {
    previewTotalDisplay.value = ''
    return
  }
  previewTimer = setTimeout(() => {
    previewTimer = null
    void runPreview()
  }, PREVIEW_DEBOUNCE_MS)
}

watch(
  () => props.visible,
  (open) => {
    if (open) {
      resetForm()
      lockBackgroundScroll()
    } else {
      taxDropdownOpen.value = false
      clearPreviewTimer()
      unlockBackgroundScroll()
    }
  },
)

watch(
  () => [amountText.value, taxMode.value, props.orderPrimaryId, props.lineIndex] as const,
  () => {
    if (!props.visible) return
    schedulePreview()
  },
)

function onClose() {
  if (props.submitting) return
  taxDropdownOpen.value = false
  emit('close')
}

function toggleTaxDropdown() {
  if (props.submitting) return
  taxDropdownOpen.value = !taxDropdownOpen.value
}

function selectTaxOption(value: RefundTaxMode) {
  taxMode.value = value
  taxDropdownOpen.value = false
}

function onDocPointerDown(e: MouseEvent) {
  if (!taxDropdownOpen.value) return
  const root = taxSelectWrapRef.value
  if (root && e.target instanceof Node && !root.contains(e.target)) {
    taxDropdownOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('mousedown', onDocPointerDown)
  if (props.visible) lockBackgroundScroll()
})

onUnmounted(() => {
  document.removeEventListener('mousedown', onDocPointerDown)
  clearPreviewTimer()
  unlockBackgroundScroll()
})

function onAmountInput(e: Event) {
  const el = e.target as HTMLInputElement
  let raw = el.value.replace(/[^0-9.$]/g, '')
  const dollarCount = (raw.match(/\$/g) ?? []).length
  if (dollarCount > 1) {
    raw = raw.replace(/\$/g, '')
    raw = `$${raw}`
  } else if (!raw.startsWith('$') && raw.length > 0 && /\d/.test(raw)) {
    raw = `$${raw.replace(/\$/g, '')}`
  }
  const parts = raw.replace(/^\$/, '').split('.')
  if (parts.length > 2) {
    raw = `$${parts[0]}.${parts.slice(1).join('')}`
  }
  const afterDot = raw.includes('.') ? raw.split('.')[1] ?? '' : ''
  if (afterDot.length > 2) {
    const [whole] = raw.replace(/^\$/, '').split('.')
    raw = `$${whole}.${afterDot.slice(0, 2)}`
  }
  amountText.value = raw
  amountErrors.value = []
  el.value = raw
}

function onAmountBlur() {
  const n = amountNum.value
  if (!amountText.value.trim() || n <= 0) {
    if (!amountText.value.trim()) return
    amountText.value = formatMoney(Math.max(0, n))
    return
  }
  amountText.value = formatMoney(n)
}

function onNoteInput(e: Event) {
  const el = e.target as HTMLTextAreaElement
  let next = el.value
  if (next.length > NOTE_MAX) {
    next = next.slice(0, NOTE_MAX)
    el.value = next
  }
  note.value = next
}

function validate(): boolean {
  const n = amountNum.value
  const next: string[] = []
  if (!amountText.value.trim() || n <= 0) {
    next.push(ERR_GT_ZERO)
  } else if (refundableNum.value > 0 && n > refundableNum.value + 1e-9) {
    next.push(ERR_EXCEED)
  }
  amountErrors.value = next
  return next.length === 0
}

function onSubmit() {
  if (props.submitting) return
  if (!validate()) {
    // Toast only for missing/invalid amount; over-limit keeps inline ERR_EXCEED only
    if (amountErrors.value.includes(ERR_GT_ZERO)) {
      emit('toast', {
        type: 'warning',
        message: 'Please complete all required fields.',
      })
    }
    return
  }
  emit('submit', {
    amount: amountNum.value,
    taxMode: taxMode.value,
    note: note.value.trim(),
  })
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="refund-modal-root"
      role="presentation"
    >
      <div
        class="refund-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="refund-modal-title"
        @click.stop
      >
        <header class="refund-modal-head">
          <h2 id="refund-modal-title">Refund</h2>
          <button
            type="button"
            class="refund-modal-x"
            aria-label="Close"
            :disabled="submitting"
            @click="onClose"
          >
            <img :src="imgClose" class="refund-modal-x-ic" alt="" />
          </button>
        </header>

        <div class="refund-modal-scroll">
        <div class="refund-modal-body">
          <div class="refund-summary">
            <p class="refund-summary-line">Amt. Paid: {{ amountPaid }}</p>
            <p class="refund-summary-line">Refundable Amount: {{ refundableAmount }}</p>
            <p class="refund-summary-line">Refunded Amt.: {{ refundedAmount }}</p>
          </div>

          <div class="refund-field">
            <label class="refund-label refund-label--amount" for="refund-amount-input">
              <span class="refund-req" aria-hidden="true">*</span>
              Amount for refund:
            </label>
            <div class="refund-input-wrap" :class="{ 'refund-input-wrap--error': hasAmountError }">
              <input
                id="refund-amount-input"
                class="refund-input"
                type="text"
                inputmode="decimal"
                autocomplete="off"
                :value="amountText"
                :disabled="submitting"
                placeholder="Enter the amount for refund"
                @input="onAmountInput"
                @blur="onAmountBlur"
              />
              <img
                v-if="hasAmountError"
                :src="errorIcon"
                alt=""
                class="refund-error-icon"
              />
            </div>
          </div>

          <div class="refund-field">
            <label class="refund-label refund-label--tax" id="refund-tax-label">
              <span class="refund-req" aria-hidden="true">*</span>
              Tax
            </label>
            <div
              ref="taxSelectWrapRef"
              class="refund-select-wrap"
              :class="{ 'refund-select-wrap--open': taxDropdownOpen }"
            >
              <button
                type="button"
                class="refund-select-trigger"
                aria-haspopup="listbox"
                :aria-expanded="taxDropdownOpen"
                aria-labelledby="refund-tax-label"
                :disabled="submitting"
                @click="toggleTaxDropdown"
              >
                <span class="refund-select-value">{{ taxModeLabel }}</span>
                <img
                  :src="imgDown"
                  class="refund-select-chevron"
                  :class="{ 'refund-select-chevron--open': taxDropdownOpen }"
                  alt=""
                />
              </button>
              <div
                v-if="taxDropdownOpen"
                class="refund-tax-dropdown"
                role="listbox"
                aria-labelledby="refund-tax-label"
              >
                <div
                  v-for="opt in resolvedTaxOptions"
                  :key="opt.value"
                  class="refund-tax-option"
                  :class="{ selected: taxMode === opt.value }"
                  role="option"
                  :aria-selected="taxMode === opt.value"
                  @click="selectTaxOption(opt.value)"
                >
                  {{ opt.label }}
                </div>
              </div>
            </div>
          </div>

          <div class="refund-field">
            <label class="refund-label refund-label--total" for="refund-total-display">Total</label>
            <input
              id="refund-total-display"
              class="refund-input refund-input--readonly"
              type="text"
              :value="totalDisplay"
              readonly
              tabindex="-1"
            />
            <div v-if="hasAmountError" class="refund-total-errors" role="alert">
              <p
                v-for="(msg, idx) in amountErrors"
                :key="idx"
                class="refund-field-error"
              >
                {{ msg }}
              </p>
            </div>
          </div>

          <div class="refund-field refund-field--note">
            <label class="refund-label refund-label--note" for="refund-note-input">Note for refund:</label>
            <textarea
              id="refund-note-input"
              class="refund-textarea"
              :value="note"
              :disabled="submitting"
              :maxlength="NOTE_MAX"
              placeholder=""
              @input="onNoteInput"
            />
            <div class="refund-note-count" aria-live="polite">{{ noteCount }}/{{ NOTE_MAX }}</div>
          </div>
        </div>
        </div>

        <footer class="refund-modal-foot">
          <button
            type="button"
            class="refund-btn refund-btn-outline"
            :disabled="submitting"
            @click="onClose"
          >
            <span class="refund-btn-label refund-btn-label--cancel">Cancel</span>
          </button>
          <button
            type="button"
            class="refund-btn refund-btn-solid"
            :disabled="submitting"
            @click="onSubmit"
          >
            <span
              class="hm-modal-btn-content"
              :class="{ 'hm-modal-btn-content--loading': submitting }"
            >
              <span class="refund-btn-label refund-btn-label--submit">Submit</span>
              <ModalButtonSpinner v-if="submitting" />
            </span>
          </button>
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.refund-modal-root {
  position: fixed;
  inset: 0;
  z-index: 12600;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(55, 55, 55, 0.72);
  overscroll-behavior: none;
  touch-action: none;
  font-family:
    system-ui,
    -apple-system,
    'Segoe UI',
    Roboto,
    Arial,
    sans-serif;
}

.refund-modal {
  box-sizing: border-box;
  position: relative;
  display: flex;
  flex-direction: column;
  width: 530px;
  min-width: min(530px, 100%);
  max-width: min(530px, calc(100vw - 48px));
  height: 692px;
  max-height: min(692px, calc(100vh - 48px));
  padding: 24px;
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #e0e0e0;
  box-shadow: none;
  overflow: hidden;
  touch-action: auto;
  overscroll-behavior: contain;
}

.refund-modal-head {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 24px;
  margin: 0 0 16px;
}

.refund-modal-head h2 {
  box-sizing: border-box;
  margin: 0;
  width: 55px;
  height: 24px;
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui,
    sans-serif;
  font-weight: 600;
  font-size: 16px;
  color: #000000;
  line-height: 24px;
  text-align: left;
  font-style: normal;
  white-space: nowrap;
}

.refund-modal-x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  line-height: 1;
  border-radius: 0;
}

.refund-modal-x:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.refund-modal-x-ic {
  width: 16px;
  height: 16px;
  display: block;
  object-fit: contain;
}

.refund-modal-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  touch-action: pan-y;
  display: flex;
  flex-direction: column;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.refund-modal-scroll::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}

.refund-modal-body {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.refund-summary {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0;
}

.refund-summary-line {
  margin: 0;
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui,
    sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  line-height: 20px;
  text-align: left;
  font-style: normal;
}

.refund-field {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.refund-field--note {
  flex: 0 0 auto;
  gap: 0;
}

.refund-field--note .refund-label {
  margin-bottom: 16px;
}

.refund-label {
  margin: 0;
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui,
    sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  line-height: 20px;
  text-align: left;
  font-style: normal;
}

.refund-req {
  color: #e95646;
  margin-right: 2px;
}

.refund-label--amount {
  box-sizing: border-box;
  height: 20px;
  color: #000000;
  white-space: nowrap;
}

.refund-label--tax {
  box-sizing: border-box;
  height: 20px;
  font-family: PingFangSC, 'PingFang SC', SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont,
    system-ui, sans-serif;
  color: #000000;
  white-space: nowrap;
}

.refund-label--tax .refund-req {
  color: #e54848;
}

.refund-label--total {
  box-sizing: border-box;
  width: 33px;
  height: 20px;
  font-family: PingFangSC, 'PingFang SC', SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont,
    system-ui, sans-serif;
  color: #000000;
  white-space: nowrap;
}

.refund-label--note {
  box-sizing: border-box;
  width: 103px;
  height: 20px;
  color: #000000;
  text-align: center;
  white-space: nowrap;
}

.refund-input,
.refund-textarea {
  box-sizing: border-box;
  width: 482px;
  max-width: 100%;
  margin: 0;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  background: #ffffff;
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui,
    sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #000000;
  outline: none;
}

.refund-input-wrap {
  position: relative;
  box-sizing: border-box;
  width: 482px;
  max-width: 100%;
  height: 32px;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  background: #ffffff;
}

.refund-input-wrap:focus-within:not(.refund-input-wrap--error) {
  border-color: #000000;
}

.refund-input-wrap--error {
  border-color: #ff4d4f;
}

.refund-input-wrap .refund-input {
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 16px;
  padding: 6px 40px 6px 14px;
}

.refund-input-wrap .refund-input:focus {
  outline: none;
  border: none;
}

.refund-input {
  height: 32px;
  padding: 6px 14px;
}

.refund-input--readonly {
  background: #f5f6fb;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui,
    sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #cccccc;
  line-height: 16px;
  text-align: left;
  font-style: normal;
  cursor: default;
}

.refund-textarea:focus:not(:disabled) {
  border-color: #000000;
}

.refund-error-icon {
  position: absolute;
  top: 50%;
  right: 12px;
  width: 16px;
  height: 16px;
  min-width: 16px;
  min-height: 16px;
  transform: translateY(-50%);
  object-fit: contain;
  pointer-events: none;
}

.refund-total-errors {
  display: flex;
  flex-direction: column;
  gap: 0;
  box-sizing: border-box;
  width: 466px;
  max-width: 100%;
  min-height: 40px;
  height: auto;
}

.refund-field-error {
  margin: 0;
  box-sizing: border-box;
  width: 466px;
  max-width: 100%;
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui,
    sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #e95646;
  line-height: 20px;
  text-align: left;
  font-style: normal;
}

.refund-select-wrap {
  position: relative;
  box-sizing: border-box;
  width: 482px;
  max-width: 100%;
  z-index: 2;
}

.refund-select-wrap--open {
  z-index: 5;
}

.refund-select-trigger {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  height: 32px;
  margin: 0;
  padding: 6px 14px;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  background: #ffffff;
  cursor: pointer;
  text-align: left;
}

.refund-select-trigger:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.refund-select-trigger:focus,
.refund-select-trigger:focus-visible {
  outline: none;
  border-color: #000000;
}

.refund-select-wrap--open .refund-select-trigger {
  border-color: #000000;
}

.refund-select-value {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui,
    sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #000000;
}

.refund-select-chevron {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  min-width: 16px;
  min-height: 16px;
  object-fit: contain;
  pointer-events: none;
  transition: transform 0.2s ease;
}

.refund-select-chevron--open {
  transform: rotate(180deg);
}

.refund-tax-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 4px 0;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.refund-tax-option {
  padding: 8px 16px;
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui,
    sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  color: #000000;
  cursor: pointer;
  transition: background 0.2s;
}

.refund-tax-option:hover {
  background: #f5f5f5;
}

.refund-tax-option.selected {
  font-weight: 600;
}

.refund-textarea {
  width: 482px;
  max-width: 100%;
  height: 120px;
  min-height: 120px;
  max-height: 120px;
  padding: 12px 14px;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  background: #ffffff;
  resize: none;
}

.refund-note-count {
  box-sizing: border-box;
  align-self: flex-end;
  height: 20px;
  margin: 12px 0 0;
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui,
    sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  line-height: 20px;
  text-align: left;
  font-style: normal;
  white-space: nowrap;
}

.refund-modal-foot {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 24px;
  padding-top: 0;
}

.refund-btn {
  box-sizing: border-box;
  width: 80px;
  min-width: 80px;
  max-width: 80px;
  height: 32px;
  min-height: 32px;
  max-height: 32px;
  margin: 0;
  padding: 0;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.refund-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.refund-btn-outline {
  background: #ffffff;
  border: 1px solid #000000;
}

.refund-btn-outline:hover:not(:disabled) {
  background: #000000;
}

.refund-btn-outline:hover:not(:disabled) .refund-btn-label--cancel {
  color: #ffffff;
}

.refund-btn-solid {
  background: #000000;
  border: none;
}

.refund-btn-solid:hover:not(:disabled) {
  background: #484848;
}

.refund-btn-label {
  box-sizing: border-box;
  display: block;
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui,
    sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 24px;
  font-style: normal;
  white-space: nowrap;
}

.refund-btn-label--cancel {
  width: 48px;
  height: 24px;
  color: #000000;
  text-align: right;
}

.refund-btn-label--submit {
  width: 50px;
  height: 24px;
  color: #ffffff;
  text-align: left;
}
</style>
